import React from 'react';
import BaseTableCard from 'components/atoms/base-table-card';
import BaseGrid from 'components/atoms/base-grid';
import { useTranslation } from 'react-i18next';
import BaseTitledCard from 'components/atoms/base-titled-card';
import ThresholdsViewForm from 'components/molecules/thresholds/view-form';
import ClinicLog from 'components/molecules/admin-panel/forms/clinic-log';
import useUser from 'utils/useUser';

import ClinicInstructionsCard from '../instructions-card';
import ClinicHeaderInfo from '../header-info';
import { useClinicViewForm } from './hook';
import ClinicsChildrenTable from './children-clinics';
import ClinicsParentTable from './parent-clinic';
import CareAgentsView from './careAgentsView';
import ClinicInvoicesForm from '../invoices-info';

import type { Thresholds } from 'infrastructure/classes/thresholds';
import type { Clinic } from 'infrastructure/classes/clinic';

interface IProps {
  info: Clinic;
  thresholds: Thresholds;
}

const ClinicViewForm: React.FC<IProps> = (props) => {
  const { info, thresholds } = props;

  const { t } = useTranslation();

  const { isAdmin, hasBillerRole, isAgencyDoctor } = useUser();

  const canView = isAdmin || (hasBillerRole && isAgencyDoctor);
  const canEdit = isAdmin && !info.parentClinicGuid;

  const {
    contactInformation,
    clinicAddress,
    clinicDetails,
    clinicMappings,
    representatives,
  } = useClinicViewForm({ info });

  const isChild =
    Boolean(info?.parentClinicName) || Boolean(info?.parentClinicGuid);

  return (
    <BaseGrid rowGap={40}>
      <ClinicHeaderInfo info={info} />
      {info?.childClinicsGuid?.length > 0 && (
        <ClinicsChildrenTable parentGuid={info.guid} />
      )}

      {isChild && info.parentClinicGuid && (
        <ClinicsParentTable parentGuid={info.parentClinicGuid} />
      )}

      <BaseGrid columns={2} columnGap={20}>
        <BaseTableCard
          title={t('labels.contactInformation')}
          items={contactInformation}
        />
        <BaseTableCard title={t('labels.address')} items={clinicAddress} />
      </BaseGrid>
      <BaseTableCard
        title={t('labels.details')}
        columns={2}
        items={clinicDetails}
      />
      <ClinicInstructionsCard clinicGuid={info.guid} info={info} />

      <BaseGrid columns={2} columnGap={20}>
        <BaseTableCard
          title={t('labels.clinicMappings')}
          items={clinicMappings}
        />
        <BaseTableCard
          title={t('labels.representatives')}
          items={representatives}
        />
      </BaseGrid>
      <CareAgentsView info={info} />
      {canView && (
        <ClinicInvoicesForm canEdit={canEdit} clinicGuid={info.guid} />
      )}
      <BaseTitledCard title={t('labels.thresholds')}>
        <ThresholdsViewForm
          data={thresholds}
          hasBloodGlucoseType
          hasWeightType
          hasSPO2Type
          hasBloodPressureType
          hasPeakFlowType
          hasSleepMatType
        />
      </BaseTitledCard>

      <ClinicLog clinicGuid={info.guid} />
    </BaseGrid>
  );
};

export default ClinicViewForm;
